import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, CornerDownLeft, Package, Tag, Users, Building2, ShoppingCart,
  FileText, Warehouse, Zap, Store, MessageSquare, Lightbulb, Loader2,
} from "lucide-react";
import { V2_NAV_FLAT, V2_QUICK_COMMANDS } from "@/v2/nav";
import { api } from "@/lib/api";

const ICON_MAP = {
  package: Package, tag: Tag, users: Users, building: Building2,
  "shopping-cart": ShoppingCart, file: FileText, warehouse: Warehouse,
  zap: Zap, store: Store, "message-square": MessageSquare, lightbulb: Lightbulb,
  search: Search,
};

export function CommandPalette({ open, onClose }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const [serverResults, setServerResults] = useState({});
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setSel(0);
      setServerResults({});
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  // Debounced universal search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const term = q.trim();
    if (term.length < 2) { setServerResults({}); setSearching(false); return; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(term)}&limit_per_type=4`);
        setServerResults(r.data?.results || {});
      } catch { setServerResults({}); }
      finally { setSearching(false); }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, open]);

  // Static navigation matches
  const navItems = useMemo(() => {
    const all = [...V2_NAV_FLAT, ...V2_QUICK_COMMANDS];
    const term = q.trim().toLowerCase();
    if (!term) return all;
    return all.filter((it) => it.label.toLowerCase().includes(term) || it.group.toLowerCase().includes(term));
  }, [q]);

  // Compose grouped list: navigation + server search
  const groups = useMemo(() => {
    const g = [];
    if (navItems.length > 0) g.push({ label: "Navigate", items: navItems.map((it) => ({ type: "nav", ...it })) });
    for (const [entityType, hits] of Object.entries(serverResults)) {
      if (!hits || hits.length === 0) continue;
      const label = (hits[0]?.type_label || entityType).toString();
      g.push({
        label: `${label}s`,
        items: hits.map((h) => ({
          type: "search", key: `${entityType}-${h.entity_id}`,
          label: h.title, sub: h.subtitle, icon: ICON_MAP[h.icon] || Search,
          to: h.route, meta: h.meta,
        })),
      });
    }
    return g;
  }, [navItems, serverResults]);

  const flatSelectable = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
      else if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, flatSelectable.length - 1)); }
      else if (e.key === "ArrowUp")   { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
      else if (e.key === "Enter")     {
        e.preventDefault();
        const t = flatSelectable[sel];
        if (t) { nav(t.to); onClose(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, sel, flatSelectable, nav, onClose]);

  if (!open) return null;

  let runningIndex = -1;
  const hasContent = groups.some((g) => g.items.length > 0);

  return (
    <>
      <div className="v2-cmd-backdrop" onClick={onClose} data-testid="cmd-backdrop" />
      <div className="v2-cmd" role="dialog" aria-label="Command palette" data-testid="command-palette">
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "0 20px", borderBottom: "1px solid var(--v2-border)" }}>
          {searching ? <Loader2 size={16} className="v2-spin" color="var(--v2-primary-2)" /> : <Search size={16} color="var(--v2-text-muted)" />}
          <input
            ref={inputRef}
            className="v2-cmd-search"
            placeholder="Search orders, products, vendors, workflows… or navigate"
            value={q}
            onChange={(e) => { setQ(e.target.value); setSel(0); }}
            data-testid="cmd-input"
            style={{ borderBottom: 0, padding: "16px 0" }}
          />
          <kbd className="v2-kbd">ESC</kbd>
        </div>
        <div className="v2-cmd-list" ref={listRef} data-testid="cmd-list">
          {!hasContent && (
            <div style={{ padding: 30, textAlign: "center", color: "var(--v2-text-muted)", fontSize: 13 }}>
              {q ? `No matches for "${q}"` : "Start typing to search across everything…"}
            </div>
          )}
          {groups.map((group) => (
            <div key={group.label}>
              <div className="v2-cmd-group-label">{group.label}</div>
              {group.items.map((it) => {
                runningIndex++;
                const idx = runningIndex;
                const selected = idx === sel;
                const Icon = it.icon || Search;
                return (
                  <div
                    key={it.key || it.to}
                    className={`v2-cmd-item ${selected ? "selected" : ""}`}
                    onMouseEnter={() => setSel(idx)}
                    onClick={() => { nav(it.to); onClose(); }}
                    data-testid={`cmd-item-${(it.key || it.label || "").toLowerCase().replace(/\W+/g, "-")}`}
                  >
                    <Icon size={16} strokeWidth={1.8} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.label}</div>
                      {it.sub && <div style={{ fontSize: 11, color: "var(--v2-text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.sub}</div>}
                    </div>
                    {!it.sub && <span style={{ fontSize: 11, color: "var(--v2-text-muted)" }}>{it.group}</span>}
                    {selected && <CornerDownLeft size={13} color="var(--v2-primary-2)" />}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
